/* eslint-disable react/no-danger */
import { faEye, faEyeSlash, faLinkSimple } from '@fortawesome/pro-light-svg-icons';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../shared/components/alert/Alert';
import { Button } from '../../../shared/components/button/Button';
import { Link } from '../../../shared/components/link/Link';
import ChevronBackBtn from '../../../shared/components/styledComponents/chevronBackBtn/ChevronBackBtn';
import { AuthContext } from '../../../shared/context/auth-context';
import { GreenCheckOverlay } from '../../../shared/modal/greenCheckOverlay/GreenCheckOverlay';
import { PasswordState } from './types';
import { usePasswordChange } from './usePasswordChange';
import './SecurityDetails.css';

type ChangePasswordRightMenuProps = {
  hide: boolean;
  profile: any;
  changeMenu: (_menuId: number, _action: string) => void;
};

export function ChangePasswordRightMenu({
  hide,
  profile,
  changeMenu,
}: ChangePasswordRightMenuProps) {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const {
    isAfterSubmit,
    passwordFields,
    passwordState,
    showGreenCheck,
    errorSummary,
    getFieldError,
    handleChangePassword,
    handlePasswordChange,
    handleFieldBlur,
    announcement,
  } = usePasswordChange();

  const renderPasswordField = useCallback(
    (
      fieldName: keyof PasswordState,
      fieldDescription: string,
      showPassword: boolean,
      toggleShowPassword: () => void,
      rulesDescription?: string
    ) => {
      const { hasError, message } = getFieldError(fieldName);
      const formattedContent = rulesDescription?.replace(/\n/g, '<br>');

      return (
        <fieldset
          key={fieldName}
          className={`password_container${hasError ? ' error' : ''}`}
          aria-labelledby={`edit-title ${fieldName}-legend`}
          aria-describedby={formattedContent ? `password_field_rules_${fieldName}` : undefined}
        >
          <legend className="not_logged_label password_field_legend" id={`${fieldName}-legend`}>
            {hasError && isAfterSubmit && t('error')}
            {fieldDescription}
            {t('input.label.required')}
          </legend>

          {formattedContent && (
            <span
              id={`password_field_rules_${fieldName}`}
              className="password_field_rules"
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
          )}
          <div className="password_field_input_wrapper">
            <input
              className="code_input password_field_input"
              type={showPassword ? 'text' : 'password'}
              name={fieldName}
              id={fieldName}
              value={passwordState[fieldName] as string}
              onChange={handlePasswordChange}
              onBlur={handleFieldBlur}
              aria-labelledby={`${fieldName}-legend`}
            />
            <Button
              ariaPressed={showPassword}
              ariaLabel={
                showPassword
                  ? t('password_visible.click_to_hide')
                  : t('password_hidden.click_to_show')
              }
              onClick={toggleShowPassword}
              icon={{
                faIconDefinition: showPassword ? faEye : faEyeSlash,
                color: 'white',
                size: 'sm',
                className: 'icon',
              }}
              variant="ghost"
              color="pink"
              className="password_field_preview_button"
            />
          </div>
          <Alert
            id={`${fieldName}-error-alert`}
            isVisible={hasError}
            message={message}
            type="error"
            variant="outline"
            size="md"
          />
        </fieldset>
      );
    },
    [passwordState, handlePasswordChange, handleFieldBlur, t, getFieldError, isAfterSubmit]
  );

  return (
    profile && (
      <>
        {showGreenCheck && <GreenCheckOverlay text={t('saved_changes_successfully')} />}
        <fieldset
          className="edit-field fs-exclude password_container"
          aria-labelledby="edit-title"
          id="change_password_container"
        >
          {hide && (
            <div className="btn-cont ">
              <ChevronBackBtn onClick={() => changeMenu(1, 'back')} />
            </div>
          )}
          <h2 className="edit-title" id="edit-title">
            {t('S026')}
          </h2>
          {errorSummary.length > 0 && isAfterSubmit && (
            <Alert
              id="error_summary_change_password"
              isAssessive
              isVisible
              message={
                <ol>
                  {errorSummary
                    .sort((a, b) => {
                      const order = ['currentPassword', 'newPassword', 'confirmNewPassword'];
                      return order.indexOf(a) - order.indexOf(b);
                    })
                    .map((error) => (
                      <li key={error}>
                        <a href={`#${error}`}>
                          {t(
                            passwordFields.find((field) => field.name === error)?.descriptionKey
                          ) || t('error')}
                        </a>
                      </li>
                    ))}
                </ol>
              }
              title={{
                text:
                  errorSummary.length > 1
                    ? t('error.summary.title.fields.many', { errorCount: errorSummary.length })
                    : t('error.summary.title.fields.one'),
                showTitle: true,
              }}
              type="error"
            />
          )}
          <form onSubmit={handleChangePassword}>
            {passwordFields.map((field) =>
              renderPasswordField(
                field.name,
                t(field.descriptionKey),
                field.showPassword,
                field.toggleFunction,
                field.rulesDescription
              )
            )}
            <Link
              href="/reset"
              color="blue"
              icon={{ faIconDefinition: faLinkSimple }}
              onClick={() => {
                auth.logout({ shouldRedirect: false });
              }}
              isWrapText
            >
              {t('settings.security.changePassword.link')}
            </Link>
            <Button type="submit" className="wide margin-top-24">
              {t('S026')}
            </Button>
          </form>
        </fieldset>
        <div
          aria-live="assertive"
          aria-atomic="true"
          role="alert"
          className="visuallyHidden"
          id="password-validation-announcement"
        >
          {announcement}
        </div>
      </>
    )
  );
}
